import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PageLoader } from '../ui/PageLoader';
import { PageHeader } from '../ui/PageHeader';
import FeatureTip from '../onboarding/FeatureTip';
import PageShell from '../ui/PageShell';
import type { Club } from '../../types/discoveryBadges';
import { useAuth } from '../../contexts/AuthContext';

const ClubsDirectoryPage: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isOrganizer } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.listClubs({ search: q || '', page: 1, limit: 30 });
      setClubs(result.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageShell width="md">
        <PageHeader
          eyebrow="Community"
          title="Clubs"
          description="Find your people. Marque clubs and communities on Bilt."
          actions={
            isAuthenticated && isOrganizer ? (
              <Button type="button" variant="primary" onClick={() => navigate('/clubs/new')}>
                Create club
              </Button>
            ) : null
          }
        />
        <FeatureTip tipId="clubs_directory" className="mb-4">
          Join communities connected to the cars, events, and people you care about.
        </FeatureTip>
        {isAuthenticated && !isOrganizer ? (
          <p className="text-sm text-gray-500 mb-4">
            Creating clubs requires organizer access. Ask an admin for an organizer invite or capability grant.
          </p>
        ) : null}
        <form
          className="flex gap-2 mb-6"
          onSubmit={(e) => {
            e.preventDefault();
            const q = search.trim();
            setActiveSearch(q);
            void load(q);
          }}
        >
          <input
            className="input flex-1"
            placeholder="Search clubs or regions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search clubs"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>
        {loading ? <PageLoader message="Loading clubs" /> : null}
        {error ? (
          <EmptyState
            title="Couldn&apos;t load clubs"
            description={error}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => void load(activeSearch)}>
                Try again
              </Button>
            }
          />
        ) : null}
        {!loading && !error && clubs.length === 0 ? (
          <EmptyState
            imageSrc={activeSearch ? undefined : '/images/brand/empty-clubs-overlook.jpg'}
            imageAlt=""
            title={activeSearch ? `No clubs match "${activeSearch}"` : 'No clubs yet'}
            description={
              activeSearch
                ? 'Try a different name or region, or create a club if you have organizer access.'
                : 'Be the first to list a club.'
            }
            action={
              isAuthenticated && isOrganizer ? (
                <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => navigate('/clubs/new')}>
                  Create club
                </Button>
              ) : undefined
            }
          />
        ) : !loading && !error ? (
          <ul className="space-y-3">
            {clubs.map((club) => (
              <li key={club.id}>
                <Link
                  to={`/clubs/${club.slug}`}
                  className="card-interactive block p-4 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {club.logo_path ? (
                        <img
                          src={apiService.getFileUrl(club.logo_path) || club.logo_path}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full bg-gradient-brand-tile flex items-center justify-center text-sm font-bold text-white shrink-0"
                          aria-hidden
                        >
                          {(club.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h2 className="heading-card">{club.name}</h2>
                        <p className="text-sm text-gray-400 mt-1">
                          {[club.region, club.club_type].filter(Boolean).join(' · ')}
                        </p>
                        {club.description ? (
                          <p className="text-sm text-gray-500 mt-2 line-clamp-2">{club.description}</p>
                        ) : null}
                      </div>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full border shrink-0 ${
                        club.is_verified
                          ? 'border-orange-500/40 text-orange-300'
                          : 'border-white/[0.08] text-gray-400'
                      }`}
                    >
                      {club.is_verified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{club.member_count} members</p>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
    </PageShell>
  );
};

export default ClubsDirectoryPage;
